import { useState } from 'react';
import { Button, Tooltip, message } from 'antd';
import { PushpinFilled, PushpinOutlined } from '@ant-design/icons';
import { useSidebarPins } from '../../hooks/use_sidebar_pins';

/**
 * Adds an entity or a connection to the caller's own sidebar, or takes it
 * off again (hooks/use_sidebar_pins.js). One of `entityId` / `connectionId`.
 * Dressed as the entity page's corner icon buttons (Entity.css), where it sits
 * on both the entity and the connection page.
 */
export default function PinToSidebarButton({ entityId, connectionId }) {
    const { ready, findEntityPin, findConnectionPin, pinEntity, pinConnection, unpin } = useSidebarPins();
    const [busy, setBusy] = useState(false);
    const existing = entityId != null ? findEntityPin(entityId) : findConnectionPin(connectionId);
    const label = existing ? 'Remove from sidebar' : 'Add to sidebar';

    const toggle = async () => {
        setBusy(true);
        try {
            if (existing) {
                await unpin(existing.id);
                message.success('Removed from your sidebar');
            } else {
                if (entityId != null) await pinEntity(entityId); else await pinConnection(connectionId);
                message.success('Added to your sidebar');
            }
        } catch (err) {
            message.error(err?.message || 'Could not update your sidebar');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Tooltip title={label}>
            <Button
                type="text"
                className={`entity-icon-btn${existing ? ' entity-icon-btn--pinned' : ''}`}
                aria-label={label}
                aria-pressed={!!existing}
                icon={existing ? <PushpinFilled /> : <PushpinOutlined />}
                loading={busy}
                disabled={!ready}
                onClick={toggle}
            />
        </Tooltip>
    );
}
